import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

import { useParams } from 'react-router';

import { Alert } from '@mui/material';
import { deepmerge } from '@mui/utils';
import { useQuery } from '@tanstack/react-query';
import { Formik, FormikHelpers } from 'formik';

import { ExtraButtonListProps } from 'components/buttons/ExtraButtons';
import { BbarForm } from 'components/docked/BbarForm';
import { FormDirtyModal } from 'components/modal/FormDirtyModal';
import { OneColumn } from 'components/page/OneColumn';
import { useFormContext } from 'context/FormContext';
import { useLoaderContext } from 'context/LoaderContext';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { useFocusErrorFormik } from 'hooks/useFocusErrorFormik';
import { RequestOptionsType, useRequestListagem } from 'hooks/useRequestListagem';
import { useValidation } from 'hooks/useValidation';
import { getErrorMessage } from 'util/error';

interface FormPageProps {
    children: any;
    title: string;
    values: any;
    url?: string;
    fgAtivo?: boolean | string;
    disableQuery?: boolean;
    hideBbar?: boolean;
    keepOriginalTitle?: boolean;
    disableSaveBtn?: boolean;
    onSubmit: (values: any, formik: FormikHelpers<any>) => void;
    validationSchema?: any;
    mapContentToInitialValues?: (content: any) => any;
    extraButtons?: ExtraButtonListProps | ExtraButtonListProps[];
    extraTopButtons?: ExtraButtonListProps | ExtraButtonListProps[];
    goBackButton?: boolean | string;
    extraContent?: React.ReactNode | null;
    validateOnChange?: boolean;
    validateOnSubmit?: boolean;
    queryWithId?: boolean;
}

const generateTitle = (title: string, id?: string): string => {
    const action = id ? 'Editar' : 'Inserir';

    return `${action} ${title}`;
};

const getUrl = ({ url, id, queryWithId }: { url?: string; id?: string; queryWithId: boolean }): string => {
    if (!url) {
        return '';
    }

    if (!queryWithId) {
        return url;
    }

    if (url?.endsWith('/')) {
        return `${url}${id}`;
    }

    return `${url}/${id}`;
};

export const FormPage = forwardRef(
    (
        {
            children,
            title: pageTitle,
            keepOriginalTitle,
            url,
            values,
            onSubmit,
            fgAtivo,
            validationSchema,
            mapContentToInitialValues,
            disableQuery,
            hideBbar,
            extraButtons,
            disableSaveBtn,
            goBackButton = true,
            extraContent,
            extraTopButtons = [],
            validateOnChange = false,
            validateOnSubmit = false,
            queryWithId = true,
        }: FormPageProps,
        ref,
    ) => {
        const { validRouterParams } = useValidation();
        const { id } = useParams();
        const { isLoading, setContent, content } = useFormContext();
        const RequestListagem = useRequestListagem();
        const { focusError } = useFocusErrorFormik();
        const formik = useRef(null);
        const { isUpMd } = useBreakpoints();

        let initialValues = values || {};

        const validId = validRouterParams({ id });
        const title = keepOriginalTitle ? pageTitle : generateTitle(pageTitle, id);

        const requestOptions: RequestOptionsType = {
            url: getUrl({ url, id, queryWithId }),
        };

        const { isFetching, data, error, refetch } = useQuery(
            [requestOptions],
            () => {
                const request: Promise<any> = RequestListagem(requestOptions);
                return request;
            },
            {
                enabled: Boolean(!disableQuery && ((id && validId) || !queryWithId) && url && !content),
                onSuccess: (response) => {
                    const content = response.data.data;

                    setContent(content);
                },
            },
        );

        useLoaderContext(isFetching);

        if (content) {
            initialValues = deepmerge(initialValues, content);
        }

        if (typeof mapContentToInitialValues === 'function' && content) {
            initialValues = mapContentToInitialValues(content);
        }

        const status = data?.status;
        const [msgError] = getErrorMessage(error);
        const showForm = ((id && validId) || !id) && Boolean(!error) && (!data || data.status !== 204);

        useImperativeHandle(ref, () => ({ formik: formik.current }), [formik]);

        // quando navega na mesma rota, trocando somente ID, precisa refazer a request para pegar os dados do novo ID
        useEffect(() => {
            if (validId && content) {
                refetch();
            }
        }, [id, refetch, validId]);

        return (
            <OneColumn title={title} goBackButton={goBackButton} extraButtons={extraTopButtons}>
                <React.Fragment>
                    {id && !validId && <Alert severity="error">Verifique se a URL informada é valida.</Alert>}

                    {Boolean(error) && <Alert severity="error">{msgError}</Alert>}

                    {status === 204 && <Alert severity="warning">Nenhum registro encontrado com o id {id}</Alert>}

                    {showForm && (
                        <Formik
                            initialValues={initialValues}
                            onSubmit={onSubmit}
                            enableReinitialize
                            validationSchema={validationSchema}
                            innerRef={formik}
                            validateOnChange={validateOnChange}
                            validateOnSubmit={validateOnSubmit}
                        >
                            {(formik: any) => {
                                focusError(formik.errors);

                                return (
                                    <React.Fragment>
                                        {typeof children === 'function' ? children(formik) : children}

                                        <FormDirtyModal dirty={formik.dirty} />

                                        {!hideBbar && (
                                            <BbarForm
                                                submitForm={formik.submitForm}
                                                extraContent={extraContent}
                                                extraButtons={extraButtons}
                                                disableSaveBtn={disableSaveBtn}
                                                isLoading={isLoading}
                                                fgAtivo={fgAtivo}
                                            />
                                        )}
                                    </React.Fragment>
                                );
                            }}
                        </Formik>
                    )}
                </React.Fragment>
            </OneColumn>
        );
    },
);
